import { DP_SEARCH_VIEW } from 'src/constants/dataPlatform';
import type { PageConfig } from 'src/libs/PageConfig';
import type { IJobApplication } from 'src/stores/JobApplicationStore/JobApplication';
import type {
  BUCKETS,
  LowerCaseBucketName,
} from 'src/stores/JobApplicationStore/types';

import captureEvent from '../../utils/captureEvent';
import { parseIntOrNull } from '../../utils/numbers';

import type { Certification } from './buildCaptureEventData';
import { transformRoleRequirementsIntoCertifications } from './mappers';

interface EventCaptureData {
  advertiserId: string;
  bucket: '' | BUCKETS;
  currentPage: number;
  hasRR: boolean;
  isSmallDevice: boolean;
  jobApplications: IJobApplication[];
  jobId: number | null;
  listingId: number;
  positionId: number;
  searchText: string;
  seekAdvertiserId: string;
  userId: number | null;
}

type DataPlatformSearchBucket = 'search';

interface EventCaptureContract {
  candidateId: number | null;
  certifications: Certification[];
  pageBucket: LowerCaseBucketName | DataPlatformSearchBucket;
  pageNumber: number;
  prospectId: number | null;
  seekJobApplicationId: number | null;
  isSmallDevice: boolean;
  hasRR: boolean;
  hirer: {
    advertiserId: number;
    seekAdvertiserId: number;
    userId: number | null;
  };
  jobId: number;
  listingId: number;
  positionId: number;
}

const sendCandidateListView = async (
  eventCaptureData: EventCaptureData,
  addCustomError: PageConfig['dataDogRum']['addCustomError'],
) => {
  const eventType = 'candidate_list_view';

  const {
    advertiserId,
    bucket,
    currentPage,
    hasRR,
    isSmallDevice,
    jobApplications,
    jobId,
    listingId,
    positionId,
    searchText,
    seekAdvertiserId,
    userId,
  } = eventCaptureData;

  if (jobApplications.length < 1) {
    return null;
  }

  if (!jobId) {
    addCustomError({
      error: 'EVENT_CAPTURE_MISSING_JOB_ID',
      context: {
        description: 'Missing job id',
        eventType,
        advertiserId,
        jobId,
        listingId,
        positionId,
        seekAdvertiserId,
        userId,
      },
    });
    return;
  }

  if (!positionId) {
    addCustomError({
      error: 'EVENT_CAPTURE_MISSING_POSITION_ID',
      context: {
        description: 'Missing position id',
        eventType,
        advertiserId,
        jobId,
        listingId,
        positionId,
        seekAdvertiserId,
        userId,
      },
    });
    return;
  }

  const parsedAdvertiserId: number | null = parseIntOrNull(advertiserId);
  const parsedSeekAdvertiserId: number | null =
    parseIntOrNull(seekAdvertiserId);

  if (!parsedAdvertiserId || !parsedSeekAdvertiserId) {
    addCustomError({
      error: 'EVENT_CAPTURE_MISSING_ADVERTISER_DATA',
      context: {
        description:
          'Missing advertiser data: [advertiserId, seekAdvertiserId]',
        eventType,
        advertiserId,
        jobId,
        listingId,
        positionId,
        seekAdvertiserId,
        userId,
      },
    });
    return;
  }

  const data: EventCaptureContract[] = jobApplications.map(
    (jobApplication) => ({
      seekCandidateId: parseIntOrNull(jobApplication.seekCandidateId),
      candidateId: parseIntOrNull(jobApplication.candidateId),
      certifications: transformRoleRequirementsIntoCertifications(
        jobApplication.roleRequirements,
      ),
      hasRR,
      hirer: {
        advertiserId: parsedAdvertiserId,
        seekAdvertiserId: parsedSeekAdvertiserId,
        userId,
      },
      isSmallDevice,
      jobId,
      listingId,
      pageNumber: currentPage,
      pageBucket: searchText
        ? DP_SEARCH_VIEW
        : (bucket.toLowerCase() as LowerCaseBucketName),
      prospectId: parseIntOrNull(jobApplication.id),
      positionId,
      seekJobApplicationId: parseIntOrNull(jobApplication.jobApplicationId),
    }),
  );

  try {
    await captureEvent({
      eventType,
      data,
      addCustomError,
    });
  } catch (error: any) {
    addCustomError({
      error,
      context: {
        activity: 'CAPTURE_EVENT',
        description:
          'Missing advertiser data: [advertiserId, seekAdvertiserId]',
        eventType,
        advertiserId,
        jobId,
        listingId,
        positionId,
        seekAdvertiserId,
        userId,
      },
    });
  }

  return null;
};

export default sendCandidateListView;
